import { GroupedFieldItem } from './grouped-field-item';
import { ChangedFieldItemProps } from './types';

/**
 * Renders a single changed field as "FieldName: oldValue → newValue".
 *
 * This is the single shared markup tree used by every domain (projects, team,
 * time-off). All domain-specific behavior (value formatting, currency, empty-old
 * placeholders, grouped rendering) is injected via the `strategy`.
 */
export const ChangedFieldItem = ({
  field,
  allFields,
  strategy,
  isVisible = true,
}: ChangedFieldItemProps) => {
  // Route grouped fields to the (optionally overridden) grouped renderer.
  const isGrouped = field.isGrouped || strategy.isGroupedField?.(field);
  if (isGrouped && field.subfieldChanges) {
    if (strategy.renderGrouped) {
      return <>{strategy.renderGrouped({ field, allFields, strategy })}</>;
    }
    return <GroupedFieldItem field={field} allFields={allFields} strategy={strategy} />;
  }

  const fieldName = field.fieldLocalizedName
    ? field.fieldLocalizedName
    : strategy.getLocalizedFieldName(field.field);

  const formattedOldValue = strategy.formatFieldValue(field.from, field.field);
  const formattedNewValue = strategy.formatFieldValue(field.to, field.field);

  const bothEmpty = !formattedOldValue && !formattedNewValue;
  const hasSubfields = !!(field.subfieldChanges && field.subfieldChanges.length > 0);

  // Some fields show a strikethrough placeholder when the old value is null
  // (e.g. "No department"). Resolved per-domain via the strategy.
  const emptyOldValuePlaceholder = strategy.getEmptyOldValuePlaceholder?.(field.field) ?? null;
  const hasEmptyOldValue = !formattedOldValue && !!emptyOldValuePlaceholder;

  if (bothEmpty && !hasSubfields && !hasEmptyOldValue) return null;

  const onlyNewValue = !formattedOldValue && formattedNewValue;

  const renderValue = (value: string) => strategy.renderValue(value, field.field, allFields);

  return (
    <div
      className={`w-full transition-all duration-300 ease-in-out ${
        isVisible ? 'max-h-[500px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'
      }`}
    >
      <div className="flex w-full flex-col">
        {(!bothEmpty || hasEmptyOldValue) && (
          <div className="flex w-full flex-col justify-center">
            <div className="flex flex-wrap items-baseline gap-x-1 text-sm leading-5">
              <span className="font-medium">{fieldName}:</span>
              {onlyNewValue && !hasEmptyOldValue ? (
                <span className="whitespace-nowrap">{renderValue(formattedNewValue)}</span>
              ) : (
                <span className="inline-flex flex-wrap items-baseline gap-x-1 whitespace-nowrap">
                  {formattedOldValue ? (
                    <span className="text-muted-foreground line-through">
                      {renderValue(formattedOldValue)}
                    </span>
                  ) : (
                    hasEmptyOldValue && (
                      <span className="text-muted-foreground line-through">
                        {emptyOldValuePlaceholder}
                      </span>
                    )
                  )}
                  {formattedNewValue ? (
                    <span className="whitespace-nowrap">
                      {strategy.strings.arrow} {renderValue(formattedNewValue)}
                    </span>
                  ) : (
                    formattedOldValue &&
                    hasEmptyOldValue && (
                      <span className="whitespace-nowrap">
                        {strategy.strings.arrow} {emptyOldValuePlaceholder}
                      </span>
                    )
                  )}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Render nested subfield changes if they exist */}
        {hasSubfields && !field.isGrouped && (
          <div className={`flex flex-col ${!bothEmpty ? 'mt-1' : ''}`}>
            {field.subfieldChanges?.map((subfield, index) => (
              <ChangedFieldItem
                key={`${subfield.field}-${index}`}
                field={subfield}
                allFields={allFields}
                strategy={strategy}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
